// Copyright (c) Microsoft. All rights reserved.

import React, { useState, useEffect, ChangeEvent } from 'react';
import { Switch } from '../base/Switch';
// import { Button } from '../Button';
import { Icon } from '../Icon';
import { Badge, Button } from '../base';
import { ConnectorService } from '../../libs/services/ConnectorService';
import { Connector, IMCPTool, UserConnector } from '../../libs/models/app/mcp/Tool';
import { outlookAuthService } from '../../libs/services/OutlookAuthService';
import { MCPToolItem } from '../../pages/chat/unit/MCPList';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { useApp } from '../../libs/hooks/useApp';
import { getConnectorIcon } from '../../libs/common';
import { ConfirmModal } from '../ConfirmModal';

interface ToolCapabilityParam {
  name: string;
  description: string;
  required?: boolean;
}

interface ToolCapability {
  title: string;
  description: string;
  params?: ToolCapabilityParam[];
  requiresConfirmation?: boolean;
}

interface ToolItem {
  id: string;
  name: string;
  description?: string;
  icon: string;
  iconUrl?: string; // 커넥터 타입별 아이콘 URL (표시용)
  iconUri?: string | null; // 원본 iconUri (API 전달용)
  enabled: boolean;
  count?: number;
  needAttention?: boolean;
  needAttentionReason?: string;
  connectorType?: string; // 원본 connectorType (outlook-mail 등)
  // API 추가 필드
  useCases?: string[];
  createdBy?: string | null;
  userDisplayName?: string | null;
  connector?: Connector; // 원본 Connector 데이터
  userConnectorType: 'Connector' | 'Mcp',
  tools?: IMCPTool[];
}


interface Category {
  id: string;
  label: string;
  tools?: ToolItem[];
}

type ViewMode = 'list' | 'add' | 'detail' | 'mcp-detail';

export const ToolSettingsContent: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedTool, setSelectedTool] = useState<ToolItem | null>(null);
  const [selectedMcpServer, setSelectedMcpServer] = useState<{ server: string, tools: IMCPTool[] } | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [previousView, setPreviousView] = useState<ViewMode>('list');
  // 도구 추가용 카테고리 (현재는 빈 상태)
  const [addToolCategories, setAddToolCategories] = useState<Category[]>([]);
  // 커넥터 추가 중 로딩 상태
  const [isAdding, setIsAdding] = useState<string | null>(null);
  // 커넥터 삭제 중 로딩 상태
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  // 삭제 확인 모달 대상 
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  // UserConnector 원본 데이터 (PUT API 호출용)
  const [userConnectors, setUserConnectors] = useState<UserConnector[]>([]);
  // available 커넥터 원본 데이터 (skillCount 매핑용)
  const [rawAvailableConnectors, setRawAvailableConnectors] = useState<Connector[]>([]);
  // 토글 중 로딩 상태
  const [isToggling, setIsToggling] = useState<string | null>(null);
  // MS 로그인 중 로딩 상태
  const [isConnecting, setIsConnecting] = useState(false);
  // 팝업 참조 저장
  // const [authPopup, setAuthPopup] = useState<Window | null>(null);

  // 리다이렉트 후 콜백 처리
  useEffect(() => {
    // 1. 리다이렉트 콜백 처리 (기존 방식 호환)
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const message = urlParams.get('message');

    if (status) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      console.log('Sucess');
      if (status === 'Success') {
        console.log('Connector registration successful');
        void syncAndFetchAll();
      } else {
        console.error('Connector registration failed:', message);
        if (message) {
          alert(`연동 실패: ${message}`);
        }
      }
    }

    // 2. 팝업 메시지 콜백 처리 (새로운 방식)
    const handleMessage = (event: MessageEvent) => {
      // 보안: 출처 확인 (현재 도메인과 동일해야 함)
      if (event.origin !== window.location.origin) return;

      if (typeof event.data === 'object' && event.data !== null && 'type' in event.data && (event.data as { type: string }).type === 'AUTH_RESULT') {
        const { status, message } = event.data as { status: string; message: string };

        if (status === 'Success') {
          console.log('Connector registration successful via popup');
          void syncAndFetchAll();
          setIsConnecting(false);
        } else {
          console.error('Connector registration failed:', message);
          alert(`연동 실패: ${message}`);
          setIsConnecting(false);
        }
      }

    };


    // 3. 팝업 닫힘 감지 (수동 종료 대응)
    const checkPopupClosed = setInterval(() => {
    }, 1000);


    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(checkPopupClosed);
    };
  }, []);

  const connectorService = new ConnectorService();
  // outlookAuthService는 싱글톤 인스턴스 사용 (import에서 가져옴)

  // MCP 도구 목록 가져오기 (Redux)
  const { localMcpTools: localMcpTools, connectors } = useAppSelector((state: RootState) => state.tools);

  //test 
  useEffect(() => {
    if (selectedMcpServer && Object.keys(localMcpTools).includes(selectedMcpServer.server))
      setSelectedMcpServer({ server: selectedMcpServer.server, tools: localMcpTools[selectedMcpServer.server] })
  }, [localMcpTools])

  useEffect(() => {
    if (selectedMcpServer && connectors.data.some(c => c.displayName == selectedMcpServer.server)) {
      setSelectedMcpServer({ server: selectedMcpServer.server, tools: connectors.data.filter(c => c.displayName == selectedMcpServer.server)[0].tools ?? [] });
    }
    fetchUserConnectors();
  }, [connectors])

  // MCP 상태 변경용 훅
  const apps = useApp();

  const changedMcpToolState = (event: ChangeEvent<HTMLInputElement>) => {

    if (Object.keys(localMcpTools).includes(event.target.id.split("__")[0]))
      apps.changedMcpToolState(event.target.id.split("__")[0], event.target.id.split("__")[1], event.target.checked);

    if (connectors.data.some(c => c.displayName == selectedMcpServer?.server)) {
      const mcp = connectors.data.find(c => c.displayName === selectedMcpServer?.server);
      if (mcp) {
        apps.updateConnectorTool(mcp.id ?? '', event.target.id, event.target.checked).catch((error) => {
          console.log(error);
        });

      }
    }

  }

  // MCP 서버 전체 on/off
  const changedMcpServerState = (event: ChangeEvent<HTMLInputElement>) => {
    console.log(event.target.id)
    if (Object.keys(localMcpTools).includes(event.target.id.split("__")[0])) {
      apps.changedMcpServerState(event.target.id, event.target.checked);
    }

  }

  // 이미 등록된 커넥터인지 확인 (connectorType으로 비교)
  const isRegistered = (connectorName: string): boolean => {
    console.log(userConnectors, connectorName);
    return userConnectors.some((c) => c.connectorType === connectorName);
  };

  // 등록된 UserConnector ID 가져오기
  const getRegisteredConnectorId = (connectorName: string): string | null => {
    const connector = userConnectors.find((c) => c.connectorType === connectorName);
    return connector?.id ?? null;
  };

  // 선택된 도구의 UserConnector 정보 가져오기
  const getSelectedUserConnector = (): UserConnector | null => {
    if (!selectedTool) return null;
    const checkType = selectedTool.connectorType ?? selectedTool.id;
    return userConnectors.find((c) => c.id === selectedTool.id || c.connectorType === checkType) ?? null;
  };

  // 인증이 필요한 도구인지 확인 (needAttention 기반)
  const needsAuth = (tool: ToolItem): boolean => {
    // connectorType이 있으면 connectorType으로, 없으면 id로 체크
    const checkId = tool.connectorType ?? tool.id;
    // 이름에 MS 관련 키워드가 포함되어 있는지 확인 (대소문자 무시)
    const lowerId = checkId.toLowerCase();
    const msKeywords = ['outlook', 'onedrive', 'teams', 'onenote', 'todo'];

    if (!msKeywords.some(keyword => lowerId.includes(keyword))) {
      return false; // MS 도구가 아니면 인증 섹션 표시 안 함
    }
    return true; // MS 도구면 항상 인증 섹션 표시 (연결 여부는 별도 판단)
  };

  // MS 연결 해제 핸들러
  const handleMsDisconnect = async () => {
    const userConnector = getSelectedUserConnector();
    if (!userConnector) {
      console.error('UserConnector not found');
      return;
    }

    setIsConnecting(true);
    try {
      // userName을 null로 업데이트하고 needAttention을 true로 설정
      await connectorService.updateConnector({
        ...userConnector,
        userName: null,
        credential: null, // 인증 정보 초기화
        needAttention: true,
      });

      // MSAL 로그아웃 (제거됨 - 서버측에서 처리되거나 필요 없음)

      // 목록 새로고침
      fetchUserConnectors();

      // 선택된 도구 정보 업데이트
      if (selectedTool) {
        setSelectedTool({
          ...selectedTool,
          description: '',
        });
      }
    } catch (error) {
      console.error('MS disconnect error:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // MS 로그인 핸들러 (팝업 방식)
  const handleMsLogin = () => {
    if (!selectedTool) {
      console.error('No tool selected');
      return;
    }

    setIsConnecting(true);
    try {
      const userConnector = getSelectedUserConnector();
      // connectorType 확인 (기존 커넥터가 없으면 툴 ID 사용)
      const connectorType = userConnector?.connectorType ?? selectedTool.id;

      console.log('MS Login clicked. Opening popup for:', connectorType);

      // 팝업 열기
      const popup = outlookAuthService.registerConnectorPopup(connectorType);

      if (popup) {
        // setAuthPopup(popup);
      } else {

        alert('팝업 차단을 해제해주세요.');
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('MS login error:', error);
      setIsConnecting(false);
    }
  };

  // 응답 데이터를 상태로 변환하는 공통 처리
  const processUserConnectorsResponse = (
    data: UserConnector[],
    availableList?: Connector[]
  ) => {
    if (data.length > 0) {
      // UserConnector 원본 데이터 저장 (PUT API 호출용)
      setUserConnectors(data);

      const userTools: ToolItem[] = data.map((c) => {
        // availableList에서 매칭되는 커넥터 정보 찾기 (skillCount 등의 정보 활용)
        const matchedConnector = availableList?.find(
          (ac) => ac.name === c.connectorType
        );

        return {
          id: c.id ?? c.connectorType ?? 'unknown',
          name: c.displayName ?? c.connectorType ?? 'Unknown',
          description: c.userName ?? '',
          icon: 'prompt',
          iconUrl: getConnectorIcon(c.iconUri),
          iconUri: c.iconUri, // 원본 iconUri 저장 (API 전달용)
          enabled: c.isEnabled,
          count: c.isEnabled ? (matchedConnector?.skillCount ? Number(matchedConnector.skillCount) : c.tools?.filter(c => c.enabled).length) : 0, // skillCount 반영
          needAttention: c.needAttention,
          needAttentionReason: c.needAttnetionReasonDetail ?? undefined,
          connectorType: c.connectorType ?? undefined,
          useCases: (matchedConnector?.examples ?? matchedConnector?.useCases) ?? undefined,
          createdBy: matchedConnector?.createdBy ?? null,
          userDisplayName: typeof c.userDisplayName === 'string' ? c.userDisplayName : null,
          connector: c.connector ?? matchedConnector ?? undefined, // API 응답에 포함된 connector 정보 우선 사용
          userConnectorType: c.userConnectorType,
          tools: c.tools,
        };
      });
      const connectors = userTools.filter(c => c.userConnectorType == 'Connector');
      const category: Category[] = []
      if (connectors.length > 0) {
        category.push({
          id: 'Connector',
          label: '커넥터',
          tools: connectors,
        });
      }

      const mcp = userTools.filter(c => c.userConnectorType == 'Mcp');
      if (mcp.length > 0) {
        const isEnabled = mcp.map((m) => {
          if (!m.enabled) m.count = 0;
          if (m.enabled && m.tools) {
            m.enabled = !m.tools.some(t => !t.enabled)
          }
          return m
        })
        category.push({
          id: 'Mcp',
          label: 'MCP 서버',
          tools: isEnabled,
        });
      }

      setCategories(category);
    } else {
      setUserConnectors([]);
      setCategories([]);
    }
  };

  // 도구 화면용: /api/connector/list 호출
  const fetchUserConnectors = () => {
    try {
      processUserConnectorsResponse(connectors.data, rawAvailableConnectors);
    } catch (error) {
      console.error('Failed to fetch user connectors:', error);
    }
  };

  // available 응답을 상태로 변환하는 공통 처리
  const processAvailableConnectorsResponse = (data: Connector[]) => {
    if (data && Array.isArray(data)) {
      const newTools: ToolItem[] = data.map((c) => ({
        id: c.name ?? 'unknown',
        name: c.displayName ?? c.name ?? 'Unknown',
        description: c.description ?? '',
        icon: 'prompt',
        iconUrl: getConnectorIcon(c.iconUri),
        iconUri: c.iconUri, // 원본 iconUri 저장 (API 전달용)
        enabled: true,
        count: c.skillCount ? Number(c.skillCount) : 0,
        useCases: (c.examples ?? c.useCases) ?? undefined,
        createdBy: c.createdBy ?? null,
        connector: c, // 원본 Connector 저장
        userConnectorType: c.userConnectorType,
      }));

      const newConnectors = newTools.filter(c => c.userConnectorType == 'Connector');
      const category: Category[] = []
      if (newConnectors.length > 0) {
        category.push({
          id: 'Connector',
          label: '커넥터',
          tools: newConnectors,
        });
      }

      const mcp = newTools.filter(c => c.userConnectorType == 'Mcp');
      if (mcp.length > 0) {
        category.push({
          id: 'Mcp',
          label: 'MCP 서버',
          tools: mcp,
        });
      }

      setAddToolCategories(category);
    }
  };


  // 커넥터 추가 핸들러 (MS 인증 포함)
  const handleAddConnector = (tool: ToolItem) => {
    setIsAdding(tool.id);
    try {
      // const connector: Connector = {
      //   name: tool.id,
      //   displayName: tool.name,
      //   description: tool.description ?? null,
      //   iconUri: tool.iconUri ?? null, // available API에서 받은 iconUri 전달
      //   userConnectorType: tool.userConnectorType,
      // };
      const toolId = tool.connectorType ?? tool.id;
        // 1. MS 인증이 필요한 커넥터인지 확인 (이름 기반 체크)
        const lowerId = toolId.toLowerCase();
        const msKeywords = ['outlook', 'onedrive', 'teams', 'onenote', 'todo'];

        const attlassian = ['confluence'];

        if (msKeywords.some(keyword => lowerId.includes(keyword))) {
          console.log('Opening auth popup for:', toolId);
          // 팝업 열기
          const popup = outlookAuthService.registerConnectorPopup(toolId);
          if (popup) {
            // setAuthPopup(popup);
          } else {

            alert('팝업 차단을 해제해주세요.');
          }

          return;
        } else if (attlassian.some(keyword => lowerId.includes(keyword))) {
          apps.registerConfluenceMcp(toolId);
        }

        // 2. 일반 커넥터: API 호출로 생성
        //await apps.createConnector(connector);

        // 목록 새로고침 (MS 인증이 아닌 경우) - 전체 동기화로 변경하여 상태 확실히 반영
        // await syncAndFetchAll();

    } catch (error) {
      console.error('Failed to add connector:', error);
      alert('도구 추가에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsAdding(null);
    }
  };

  // 커넥터 연결 해제 핸들러
  const handleDeleteConnector = async (connectorName: string) => {
    const connectorId = getRegisteredConnectorId(connectorName);
    if (!connectorId) {
      console.error('Connector ID not found for:', connectorName);
      return;
    }

    setIsDeleting(connectorName);
    try {
      await apps.deleteConnector(connectorId);

      // 성공 시: 목록 새로고침
      // fetchUserConnectors();
    } catch (error) {
      console.error('Failed to delete connector:', error);
      // TODO: 에러 토스트 표시
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDeleteConnectorById = (connectorId: string) => {
    if (!connectorId) return;
    setDeleteTargetId(connectorId);
  };

  const confirmDeleteConnector = async () => {
    const connectorId = deleteTargetId;
    setDeleteTargetId(null);
    if (!connectorId) return;

    setIsDeleting(connectorId);
    try {
      await apps.deleteConnector(connectorId);
      await syncAndFetchAll();
    } catch (error) {
      console.error('Failed to delete connector:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  // 초기 로드용: available과 비교해서 없는 유저 커넥터 삭제 후 목록 반환
  // available과 userConnectors를 한 번에 가져와서 중복 API 호출 방지
  const syncAndFetchAll = async () => {
    try {
      const { available, userConnectors } = await apps.syncUserConnectors();
      setRawAvailableConnectors(available);
      processUserConnectorsResponse(userConnectors.data, available);
      processAvailableConnectorsResponse(available);
    } catch (error) {
      console.error('Failed to sync connectors:', error);
    }
  };



  useEffect(() => {
    // 초기 로드: available + userConnectors 동시 조회 (고아 커넥터 삭제 포함)
    void syncAndFetchAll();
    //void syncServerMcpTools();
  }, []);

  useEffect(() => {
    // if (viewMode == 'add')
    //   syncServerMcpTools();
    // void syncAndFetchAll();
  }, [viewMode])

  // 토글 스위치 핸들러 - PUT API 호출
  const handleToggleTool = async (categoryId: string, toolId: string) => {
    // 해당 UserConnector 찾기
    const userConnector = userConnectors.find((c) => c.id === toolId);
    if (!userConnector) {
      console.error('UserConnector not found:', toolId);
      return;
    }

    const newEnabled = !userConnector.isEnabled;
    setIsToggling(toolId);
    console.log(userConnector, newEnabled);

    try {
      // PUT API 호출
      await apps.updateConnector({
        ...userConnector,
        isEnabled: newEnabled,
        tools: userConnector.tools
          ? userConnector.tools.map((c) => ({
            ...c,
            enabled: newEnabled,
          }))
          : undefined,
      });

      // userConnectors 상태 업데이트
      // setUserConnectors((prev) => prev.map((c) => (c.id === toolId ? updatedConnector : c)));
    } catch (error) {
      console.error('Failed to update connector:', error);
      // 실패 시 롤백
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === categoryId
            ? {
              ...category,
              tools: category.tools?.map((tool) => (tool.id === toolId ? { ...tool, enabled: !newEnabled } : tool)),
            }
            : category,
        ),
      );
    } finally {
      setIsToggling(null);
    }
  };

  const goToDetail = (tool: ToolItem, fromView: ViewMode) => {
    setSelectedTool(tool);
    setPreviousView(fromView);
    setViewMode('detail');
  };

  const goToMcpDetail = (server: string, tools: IMCPTool[]) => {
    setSelectedMcpServer({ server: server, tools: tools });
    setPreviousView('list');
    setViewMode('mcp-detail');
  };

  // MCP가 데스크톱 앱에서만 보이도록 조건 확인 (window.electron 사용)
  const isDesktop = window.electron?.isElectron;

  return (
    <>
      {viewMode === 'list' && (
        <div className="tool-settings-content">
          <div className="tool-settings-header">
            <h3 className="tool-settings-title">도구</h3>
            <Button
              size="small"
              variant="primary"
              prefixIcon="plus"
              className="btn-add-tool"
              onClick={() => {
                setViewMode('add');
              }}
            >
              도구 추가/삭제
            </Button>
          </div>
          <div className="tool-settings-list">
            {/* 도구가 없을 때 안내 메시지 */}
            {categories.length === 0 &&
              (!isDesktop || !localMcpTools || Object.keys(localMcpTools).length === 0) && (
                <div className="tool-empty-state">
                  <p className="tool-empty-message">도구가 없습니다. 도구 추가 버튼을 눌러 추가해주세요.</p>
                </div>
              )}

            {categories.map((category) => (
              <div key={category.id} className="tool-category tool-category-mb">
                <h4 className="tool-category-title">{category.label}</h4>
                <div className="tool-list">
                  {category.tools?.map((tool) => (
                    <div key={tool.id} className={`tool-item ${tool.needAttention ? 'need-attention' : ''}`}>
                      <div className="tool-item-left">
                        <div className="tool-icon">
                          <img src={tool.iconUrl} alt={tool.name} className="connector-icon" />
                        </div>
                        <div className="tool-info">
                          <div className="tool-name">{tool.name}</div>
                          {tool.needAttention ? (
                            <div className="tool-attention-warning">
                              <span className="attention-icon">!</span>
                              <span className="attention-text">{tool.needAttentionReason ?? '확인이 필요합니다'}</span>
                            </div>
                          ) : (
                            tool.description && <div className="tool-desc">{tool.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="tool-item-right">
                        <button
                          className="btn-tool-delete"
                          disabled={isDeleting === tool.id}
                          aria-label="도구 삭제"
                          onClick={() => {
                            handleDeleteConnectorById(tool.id);
                          }}
                        >
                          <Icon name="delete" />
                        </button>
                        {tool.needAttention ?
                          <Button size="default" variant="soft" prefixIcon='refresh' onClick={() => { handleAddConnector(tool) }}> 재연결 </Button>
                          :
                          <>
                            {tool.count !== undefined && <span className="tool-count">{tool.count}</span>}
                            < Switch
                              checked={tool.enabled}
                              disabled={isToggling === tool.id || (tool.needAttention)}
                              onChange={() => {
                                void handleToggleTool(category.id, tool.id);
                              }}
                            />

                            <button
                              className="btn-more"
                              onClick={() => {
                                console.log(category.id);
                                if (category.id == 'Mcp') goToMcpDetail(tool.name, tool.tools ?? []);
                                else goToDetail(tool, 'list');
                              }}
                            >
                              <Icon name="next" />
                            </button>
                          </>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {/* MCP Server Section - Only visible on Desktop if tools exist */}
            {isDesktop && localMcpTools && Object.keys(localMcpTools).length > 0 && (
              <div className="tool-category tool-category-mb">
                <h4 className="tool-category-title">로컬 MCP 서버</h4>
                <div className="tool-list tool-list-block">
                  <div className="mcp-server-list-box-full">
                    {Object.keys(localMcpTools).map((server) => {
                      const serverTools = localMcpTools[server];
                      const enabledCount = serverTools.filter(t => t.enabled).length;
                      const isAllEnabled = serverTools.length > 0 && enabledCount === serverTools.length;

                      return (
                        <div key={server} className="tool-item tool-item-pointer" onClick={() => { goToMcpDetail(server, serverTools); }}>
                          <div className="tool-item-left">
                            <div className="tool-icon">
                              <Badge accent='normal'>{server.slice(0, 1).toUpperCase()}</Badge>
                            </div>
                            <div className="tool-info">
                              <div className="tool-name">{server}</div>
                              <div className="tool-desc">MCP Server</div>
                            </div>
                          </div>
                          <div className="tool-item-right">
                            <span className="btn-tool-delete-spacer" aria-hidden="true" />
                            <span className="tool-count">{enabledCount}</span>
                            <div onClick={(e) => { e.stopPropagation(); }}>
                              <Switch
                                id={server}
                                checked={isAllEnabled}
                                onChange={changedMcpServerState}
                              />
                            </div>
                            <button className="btn-more">
                              <Icon name="next" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {viewMode === 'add' && (
        <div className="tool-settings-content">
          <div className="tool-settings-header">
            <div className="tool-settings-title-with-back">
              <button
                className="btn-back"
                onClick={() => {
                  setViewMode('list');
                }}
              >
                <Icon name="prev" />
              </button>
              <h3 className="tool-settings-title">도구 추가/삭제</h3>
            </div>
          </div>
          <div className="tool-settings-list">
            {addToolCategories.length === 0 && (
              <div className="tool-empty-state">
                <p className="tool-empty-message">이용 가능한 도구가 없습니다.</p>
              </div>
            )}
            {addToolCategories.map((category) => (
              <div key={category.id} className="tool-category">
                <h4 className="tool-category-title">{category.label}</h4>
                {category.id == 'Connector' &&
                  <div className="tool-list">
                    {category.tools?.map((tool) => (
                      <div key={tool.id} className="tool-add-item">
                        <div className="tool-add-item-header">
                          <div className="tool-item-left">
                            <div className="tool-icon">
                              <img src={tool.iconUrl} alt={tool.name} className="connector-icon" />
                            </div>
                            <div className="tool-info">
                              <div className="tool-name">{tool.name}</div>
                            </div>
                          </div>
                          {isRegistered(tool.id) ? (
                            <Button
                              size="small"
                              className="btn-disconnect"
                              disabled={isDeleting === tool.id}
                              onClick={() => {
                                void handleDeleteConnector(tool.id);
                              }}
                            >
                              {isDeleting === tool.id ? '제거 중...' : '제거'}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="primary"
                              disabled={isAdding === tool.id}
                              onClick={() => {
                                handleAddConnector(tool);
                              }}
                            >
                              {isAdding === tool.id ? '추가 중...' : '추가'}
                            </Button>
                          )}
                        </div>
                        <div className="tool-add-item-body">
                          <p className="tool-add-description">{tool.description}</p>
                          {tool.count !== undefined && (
                            <div className="tool-add-detail-section">
                              <button
                                className="tool-add-detail-toggle"
                                onClick={() => {
                                  goToDetail(tool, 'add');
                                }}
                              >
                                <Icon name="prompt" />
                                <span>{tool.count}</span>
                                <span>자세히</span>
                                <Icon name="next" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>}
                {category.id == 'Mcp' &&
                  <div className="tool-list">
                    {category.tools?.map((tool) => (
                      <div key={tool.name} className="tool-add-item">
                        <div className="tool-add-item-header">
                          <div className="tool-item-left">
                            <div className="tool-icon">
                              <img src={tool.iconUrl} alt={tool.name} className="connector-icon" />
                            </div>
                            <div className="tool-info">
                              <div className="tool-name">{tool.name}</div>
                            </div>
                          </div>
                          {isRegistered(tool.id) ? (
                            <Button
                              size="small"
                              className="btn-disconnect"
                              disabled={isDeleting === tool.id}
                              onClick={() => {
                                void handleDeleteConnector(tool.id);
                              }}
                            >
                              {isDeleting === tool.name ? '제거 중...' : '제거'}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="primary"
                              disabled={isAdding === tool.name}
                              onClick={() => {
                                handleAddConnector(tool);
                              }}
                            >
                              {isAdding === tool.name ? '추가 중...' : '추가'}
                            </Button>
                          )}
                        </div>
                        <div className="tool-add-item-body">
                          <p className="tool-add-description">{tool.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </div>
            ))}
          </div>
        </div>
      )}
      {viewMode === 'mcp-detail' && selectedMcpServer && (
        <div className="tool-settings-content">
          <div className="tool-settings-header">
            <div className="tool-settings-title-with-back">
              <button
                className="btn-back"
                onClick={() => {
                  setViewMode(previousView);
                  setSelectedMcpServer(null);
                }}
              >
                <Icon name="prev" />
              </button>
              <h3 className="tool-settings-title">{selectedMcpServer.server}</h3>
            </div>
          </div>

          <div className="tool-detail-content">
            <div className="mcp-server-list-box-full">
              {selectedMcpServer.tools.map((tool, index) => (
                <div key={tool.name}>
                  <MCPToolItem
                    server={selectedMcpServer.server}
                    name={tool.id ?? tool.name}
                    size="body2"
                    textOnly={true}
                    switchPosition="left"
                    switchChecked={tool.enabled}
                    switchId={tool.name}
                    onClick={changedMcpToolState}
                  />
                  <div className="mcp-server-description">
                    {tool.description}
                  </div>
                  {index < selectedMcpServer.tools.length - 1 && <hr className="dsx-hr mcp-server-hr" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {viewMode === 'detail' && selectedTool && (
        <div className="tool-settings-content">
          <div className="tool-settings-header">
            <div className="tool-settings-title-with-back">
              <button
                className="btn-back"
                onClick={() => {
                  setViewMode(previousView);
                  setSelectedTool(null);
                }}
              >
                <Icon name="prev" />
              </button>
              <h3 className="tool-settings-title">{selectedTool.name}</h3>
            </div>
          </div>
          <div className="tool-detail-content">
            {(() => {
              const connector = selectedTool.connector;
              const providerName = selectedTool.createdBy ?? connector?.createdBy ?? 'Unknown Provider';
              // description: 커넥터 설명 (UserConnector의 userName 아님)
              const toolDescription = connector?.description ?? (selectedTool.connectorType ? rawAvailableConnectors.find(c => c.name === selectedTool.connectorType)?.description : undefined) ?? '';
              const toolUseCases = connector?.examples ?? connector?.useCases ?? [];

              const capabilities: ToolCapability[] = connector?.skills?.map(skill => ({
                title: skill.displayName,
                description: skill.description,
                params: [], // 파라미터 정보는 description 텍스트에 포함되어 있음
                requiresConfirmation: skill.needApprovalToRun
              })) ?? [];

              // AI Tool 섹션은 현재 API에 없음
              const aiToolTitle = undefined;
              const aiToolFeatures: string[] = [];

              return (
                <>
                  <div className="tool-detail-header center-aligned">
                    <div className="tool-detail-icon">
                      <img src={selectedTool.iconUrl} alt={selectedTool.name} className="connector-icon" />
                    </div>
                    <div className="tool-detail-info center-aligned">
                      <h2 className="tool-detail-name">{selectedTool.name}</h2>
                      <p className="tool-detail-provider">{providerName}</p>
                    </div>
                    <div className="tool-detail-description-text">
                      <p>{toolDescription}</p>
                    </div>
                  </div>

                  {/* 인증이 필요한 도구일 경우에만 연결 섹션 표시 */}
                  {needsAuth(selectedTool) && (
                    <div className="tool-detail-section">
                      {(() => {
                        const userConnector = getSelectedUserConnector();

                        // 1. 인증 오류
                        const isAuthError = userConnector?.needAttention &&
                          userConnector?.needAttentionReason === 'Authorization';

                        // 2. 연결됨
                        const isConnected = !userConnector?.needAttention &&
                          (!!userConnector?.userName || !!userConnector?.credential);

                        if (isAuthError) {
                          return (
                            <>
                              <h3 className="tool-detail-section-title">계정 연결 오류</h3>
                              <div className="tool-detail-connection auth-error">
                                <div className="connection-error-alert">
                                  <Icon name="info" />
                                  <p>
                                    인증 오류로 인해 커넥터가 비활성화되었습니다. 다시 로그인을 시도해 주세요.
                                  </p>
                                </div>
                                {userConnector?.userName && (
                                  <div className="connection-user-info">
                                    <p className="connection-email">{userConnector.userName}</p>
                                  </div>
                                )}
                                <Button
                                  size="small"
                                  variant="primary"
                                  className="btn-reconnect"
                                  disabled={isConnecting}
                                  onClick={() => {
                                    handleMsLogin();
                                  }}
                                >
                                  {isConnecting ? '연결 중...' : '다시 연결'}
                                </Button>
                              </div>
                            </>
                          );
                        }

                        if (isConnected) {
                          return (
                            <>
                              <h3 className="tool-detail-section-title">다음으로 연결되었어요</h3>
                              <div className="tool-detail-connection connected-card">
                                <div className="connection-info">
                                  {userConnector?.userName && (
                                    <p className="connection-email">{userConnector.userName}</p>
                                  )}
                                  {userConnector?.userDisplayName && (
                                    <p className="connection-name">{userConnector.userDisplayName}</p>
                                  )}
                                  {!userConnector?.userName && !userConnector?.userDisplayName && (
                                    <p className="connection-email">연결됨</p>
                                  )}
                                </div>
                                <Button
                                  size="small"
                                  className="btn-connection-delete"
                                  disabled={isConnecting}
                                  onClick={() => {
                                    void handleMsDisconnect();
                                  }}
                                >
                                  {isConnecting ? '해제 중...' : '연결 해제'}
                                </Button>
                              </div>
                            </>
                          );
                        }

                        // 비연결 상태
                        return (
                          <>
                            <h3 className="tool-detail-section-title">계정 연결</h3>
                            <div className="tool-detail-connection not-connected">
                              <div className="connection-info">
                                <span className="connection-status warning">⚠️ Microsoft 계정 연결이 필요합니다</span>
                                <p className="connection-desc">이 도구를 사용하려면 Microsoft 계정으로 로그인해야 합니다.</p>
                              </div>
                              <Button
                                size="small"
                                variant="primary"
                                className="btn-ms-login"
                                disabled={isConnecting}
                                onClick={() => {
                                  handleMsLogin();
                                }}
                              >
                                {isConnecting ? '연결 중...' : 'Microsoft 계정으로 연결하기'}
                              </Button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {toolUseCases && toolUseCases.length > 0 && (
                    <div className="tool-detail-section">
                      <h3 className="tool-detail-section-title">이렇게 사용할 수 있어요</h3>
                      <div className="tool-usage-chips">
                        {toolUseCases.map((example, index) => (
                          <div key={index} className="usage-chip">{example}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="tool-detail-section">
                    <div className="section-header-with-badge">
                      <h3 className="tool-detail-section-title">이러한 작업을 할 수 있어요</h3>
                      <div className="tool-count-badge-large">
                        <span role="img" aria-label="tools" style={{ fontSize: '14px', marginRight: '2px' }}>🛠️</span>
                        <span>{capabilities.length > 0 ? capabilities.length : (selectedTool.count ?? 0)}</span>
                      </div>
                    </div>

                    <div className="tool-permissions">
                      {connector?.hasNeedApprovalSkills && (
                        <div className="sensitive-action-alert">
                          <div className="alert-icon-wrapper">
                            <img src="/images/dino-icon.png" alt="alert" className="dino-icon" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                            <Icon name="info" />
                          </div>
                          <div className="alert-content">
                            <h4>민감한 작업 실행 전 사용자에게 확인</h4>
                            <p>민감한 작업은 실행 전에 AI가 사용자가 원치 않은 작업을 실행하는 문제를 방지하기 위해 사용자의 확인을 받습니다.</p>
                          </div>
                        </div>
                      )}

                      <div className="permission-group-clean">
                        {capabilities.map((capability, index) => (
                          <div key={index} className="permission-item-clean">
                            <div className="permission-check">✅</div>
                            <div className="permission-content">
                              <div className="permission-title">
                                <span className="permission-title-bold">{capability.title}:</span> {capability.description}
                              </div>
                              {capability.params && capability.params.length > 0 && (
                                <ul className="permission-sublist">
                                  {capability.params.map((param, pIndex) => (
                                    <li key={pIndex}>
                                      {param.name} ({param.required ? '필수' : '선택'}) : {param.description}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {capability.requiresConfirmation && (
                                <div className="permission-note">
                                  <Icon name="info" />
                                  <span>실행 전 사용자에게 확인</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {capabilities.length === 0 && (
                          <div className="permission-item-clean">
                            <div className="permission-content">
                              <div className="permission-title">상세 정보가 없습니다.</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {aiToolTitle && (
                    <div className="tool-detail-section">
                      <h3 className="tool-detail-section-title">AI 도구가 함께 작동해요</h3>
                      <div className="ai-tool-card">
                        <div className="ai-tool-body">
                          <div className="ai-tool-icon">
                            <Icon name="prompt" />
                          </div>
                          <div className="ai-tool-content-wrapper">
                            <h4>{aiToolTitle}</h4>
                            <ul className="ai-tool-features">
                              {aiToolFeatures.map((feature, index) => (
                                <li key={index}>{feature}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
      {deleteTargetId && (
        <ConfirmModal
          title="도구 삭제"
          message={<>삭제된 도구는 &lsquo;도구 추가/삭제&rsquo;에서 다시 추가하실 수 있습니다.<br />삭제하시겠습니까?</>}
          onConfirm={() => {
            void confirmDeleteConnector();
          }}
          onCancel={() => {
            setDeleteTargetId(null);
          }}
        />
      )}
    </>
  );
};
